import { useSearchParams } from '@uhg-abyss/web/tools/reactRouterTools';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { getGeoLocationFromStorage } from '../../common/PSXHeader/SearchBar/utils';
import { SearchFilterContext } from '../../context/SearchFilterContext';
import { extractZipCodeFromPlaceName } from '../../frontends/GuestExperience/Guest.utils';
import { useSensitiveStore } from '../../store/useSensitiveStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { useUserLocation } from '../useUserLocation';

const DFO = {};

export const useGeoLocationStorage = () => {
  const [, setSearchParams] = useSearchParams();
  const [geoLocation, setGeoLocation] = useState(getGeoLocationFromStorage());
  const [userLocation, getUserLocation] = useUserLocation(DFO);

  const { zipCode, setLocationData } = useSensitiveStore(
    useShallow((state: any) => ({
      zipCode: state.sensitiveData?.location?.zipCode,
      setLocationData: state.setLocationData,
    }))
  );

  useEffect(() => {
    if (!geoLocation?.latitude || !geoLocation?.longitude) return;

    getUserLocation({
      variables: {
        longitude: geoLocation?.longitude,
        latitude: geoLocation?.latitude,
      },
    });
  }, [geoLocation?.latitude, geoLocation?.longitude]);

  useEffect(() => {
    const zipCodeFromPlaceName =
      extractZipCodeFromPlaceName(
        userLocation?.data?.userLocation?.features?.[0]?.place_name ?? ''
      ) || undefined;

    if (zipCodeFromPlaceName) {
      setLocationData({ zipCode: zipCodeFromPlaceName });
    }
  }, [userLocation]);

  const geoLocationMemo = useMemo(
    () => ({
      ...geoLocation,
      zipcode: zipCode,
      zipCode,
    }),
    [geoLocation, zipCode]
  );

  const { clearSearchFilters } = useContext(SearchFilterContext);
  const { setTypeaheadSearchStore } = useTypeaheadStore(
    useShallow((state: any) => ({
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
    }))
  );
  useEffect(() => {
    const getGeoLocation = () => {
      const newGeoLocation = getGeoLocationFromStorage();
      clearSearchFilters();
      // remove searchRadius from url and set isTypeaheadStoreValuesSet to false whenever location is changed.
      // set isDeeplinkAutoSearchRadiusQueryDone to false when location changes to trigger auto search radius query again.
      setSearchParams(
        (searchParams) => {
          if (searchParams.get('isDeeplinkAutoSearchRadiusQueryDone')) {
            searchParams.set('isDeeplinkAutoSearchRadiusQueryDone', 'false');
          }
          searchParams.delete('searchRadius');
          return searchParams;
        },
        { replace: true }
      );
      const urlParams = new URLSearchParams(window.location.search);
      const isExternalMapViewLink =
        !!urlParams.get('sectionType') && !urlParams.get('psxSource');
      if (!isExternalMapViewLink) {
        setTypeaheadSearchStore({ isTypeaheadStoreValuesSet: false });
      }
      setGeoLocation(newGeoLocation);
    };
    window.addEventListener('updateGeoLocationToStorage', getGeoLocation);
    return () => {
      window.removeEventListener('updateGeoLocationToStorage', getGeoLocation);
    };
  }, [clearSearchFilters]);

  return geoLocationMemo;
};
